import { CSSProperties, ReactNode, useMemo } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  RowData,
  useReactTable,
} from '@tanstack/react-table';
import { Box, ThemeUICSSObject } from 'theme-ui';

import { headerStyles } from '@/components/Index/ItemTableStyles';

// Per-column layout overrides, mirroring the legacy react-table 7 wrapper's
// support for `style`/`width`/`minWidth`/`maxWidth` on a column. Columns pass
// these through `columnDef.meta`, e.g. `meta: { width: 80 }`.
type ColumnLayoutMeta = {
  style?: CSSProperties;
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
};

declare module '@tanstack/react-table' {
  // Declaration merging requires an `interface` with the original type
  // parameters; it only contributes the layout fields above.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-interface
  interface ColumnMeta<TData extends RowData, TValue>
    extends ColumnLayoutMeta {}
}

// The column type consumers pass. The value generic is left open so callers
// don't have to thread a value type through every column definition.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataTableColumn<T> = ColumnDef<T, any>;

type DataTableProps<T> = {
  data: Array<T>;
  columns: Array<DataTableColumn<T>>;
  onRowClick?: (row: T) => void;
  containerStyles?: ThemeUICSSObject;
};

const itemTableStyles: ThemeUICSSObject = { py: 1, fontSize: 1, marginTop: 4 };
const tableStyles: ThemeUICSSObject = {
  borderCollapse: 'collapse',
  minWidth: '100%',
};
const headerCellStyles: ThemeUICSSObject = {
  color: 'neutralDark',
  fontWeight: 400,
  paddingX: 3,
  paddingY: 2,
  textAlign: 'left',
};
const cellStyles: ThemeUICSSObject = { paddingX: 3, paddingY: 4 };
const rowStyles: ThemeUICSSObject = {
  borderBottom: '1px solid',
  borderColor: 'secondaryHighlight',
  color: 'neutralDark',
};

const getColumnStyle = (meta: ColumnLayoutMeta = {}): CSSProperties => ({
  ...meta.style,
  minWidth: meta.minWidth,
  maxWidth: meta.maxWidth,
  width: meta.width,
});

/**
 * @tanstack/react-table v8 equivalent of the legacy react-table 7 `Table.tsx`,
 * with the same prop surface. It exists alongside `Table.tsx` while both
 * libraries coexist during the react-table 7 -> v8 migration (sc-63202);
 * consumers move over one at a time and `Table.tsx` is removed once empty.
 */
function DataTable<T>(props: DataTableProps<T>) {
  const columns = useMemo(() => props.columns, [props.columns]);
  // v8 caches each cell's accessor result on its row, and only rebuilds rows when
  // `data` changes. Copying on column change gives new rows so stale values don't stick.
  const data = useMemo(() => [...props.data], [props.data, columns]);

  const table = useReactTable({
    data,
    columns,
    // Render the raw accessor value as-is (React node or string), matching the
    // legacy react-table 7 default cell. v8's own default stringifies via
    // `.toString()`, which would turn a node-returning accessor into
    // "[object Object]". Kept inline so `T` stays in scope for inference.
    defaultColumn: { cell: (info) => info.getValue() as ReactNode },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Box sx={{ ...itemTableStyles, ...props.containerStyles }}>
      <table sx={tableStyles}>
        <thead sx={headerStyles}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  style={getColumnStyle(header.column.columnDef.meta)}
                  sx={headerCellStyles}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              sx={{
                ...rowStyles,
                cursor: props.onRowClick ? 'pointer' : undefined,
              }}
              onClick={() => props.onRowClick?.(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  style={getColumnStyle(cell.column.columnDef.meta)}
                  sx={cellStyles}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          {table.getRowModel().rows.length === 0 && (
            <tr sx={rowStyles}>
              <td
                colSpan={columns.length}
                sx={{
                  ...cellStyles,
                  textAlign: 'center',
                  color: 'neutralDark',
                }}
              >
                No data.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Box>
  );
}

export default DataTable;
